import { Link } from "react-router-dom";
import type { MapMarker } from "@/lib/types";
import { contrastText, validColor } from "@/lib/color";

interface Props {
  marker: MapMarker;
  colorMap: Record<string, string>;
  selected: boolean;
  onSelect: (no: number) => void;
}

function cateName(m: MapMarker): string {
  return String(m.cate_name || m.cate || "기타").trim();
}

function bizName(m: MapMarker): string {
  return String(m.biz_name || "사업장").trim();
}

function address(m: MapMarker): string {
  return String(m.address || m.addr || m.biz_addr || "").trim();
}

function tel(m: MapMarker): string {
  return String(m.tel || m.biz_tel || "").trim();
}

function distLabel(m: MapMarker): string {
  const d = m.dist;
  if (d == null || d === "") return "";
  const n = Number(d);
  if (!Number.isFinite(n)) return String(d);
  return n < 1 ? `${Math.round(n * 1000)}m` : `${n.toFixed(1)}km`;
}

export default function BizListItem({ marker, colorMap, selected, onSelect }: Props) {
  const cate = cateName(marker);
  const color = validColor(String(marker.color ?? "")) || validColor(colorMap[cate]) || "#17408B";
  const name = bizName(marker);
  const person = [marker.name, marker.aho].filter(Boolean).join(" ");
  const addr = address(marker);
  const phone = tel(marker);
  const dist = distLabel(marker);
  const lat = Number(marker.lat);
  const lon = Number(marker.lon);
  const routeUrl = `https://map.kakao.com/link/to/${encodeURIComponent(name)},${lat},${lon}`;

  return (
    <article
      data-no={marker.no}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(marker.no)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(marker.no);
        }
      }}
      className={`cursor-pointer rounded-lg border p-3.5 transition-colors ${
        selected
          ? "border-secondary-500 bg-secondary-50"
          : "border-background-200 bg-background-50 hover:border-background-300 hover:bg-background-100"
      }`}
    >
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 inline-flex shrink-0 items-center justify-center rounded-md px-2 py-1 text-xs font-bold"
            style={{ backgroundColor: color, color: contrastText(color) }}
          >
            {cate.slice(0, 4)}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-heading text-sm font-bold text-foreground-950">{name}</h3>
            {person && <p className="mt-0.5 truncate text-xs text-foreground-600">{person}</p>}
            {addr && (
              <p className="mt-1 flex items-start gap-1 text-xs text-foreground-500">
                <i className="ri-map-pin-line mt-0.5 shrink-0" aria-hidden="true"></i>
                <span className="line-clamp-2">{addr}</span>
              </p>
            )}
          </div>
          {dist && (
            <span className="shrink-0 whitespace-nowrap rounded-full bg-background-200/80 px-2 py-0.5 text-[11px] font-medium text-foreground-600">
              {dist}
            </span>
          )}
        </div>

        <div className="mt-3 flex items-center gap-2">
          {phone && (
            <a
              href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-phone-line" aria-hidden="true"></i>
              전화
            </a>
          )}
          <a
            href={routeUrl}
            target="_blank"
            rel="nofollow noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className="ri-navigation-line" aria-hidden="true"></i>
            길찾기
          </a>
          <Link
            to={`/members/${marker.no}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-2.5 py-1.5 text-xs font-semibold text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-user-line" aria-hidden="true"></i>
            상세
          </Link>
        </div>
      </article>
  );
}